import React from 'react';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Container from '@amzn/awsui-components-console/container';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import Badge from '@amzn/awsui-components-console/badge';
import Icon from '@amzn/awsui-components-console/icon';
import { features, PILOT_URL, SURVEY_URL, userTypes } from '../../constants';
import Roadmap from './Roadmap';
import AstVsLangHub from './AstVsLangHub';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import tsLogo from 'src/images/ts-logo.svg';
import styles from './MainContent.module.scss';

/** Number of feature cards in the 3-column top grid; remainder goes in the 2-column bottom grid */
const FEATURES_TOP_ROW_COUNT = 9;

const MainContent = () => {
  const { t } = useTranslation();

  const renderFeatureCard = (f: (typeof features)[number]) => (
    <Container key={f.titleId} fitHeight={true}>
      <SpaceBetween size="xs">
        <Box>
          <Icon name={f.iconName} size="normal" variant="link" />{' '}
          <Box variant="strong" display="inline">
            {t(f.titleId)}
          </Box>
          {f.badgeId && (
            <>
              {' '}
              <Badge color="blue">{t(f.badgeId)}</Badge>
            </>
          )}
        </Box>
        <Box variant="p" fontSize="body-m" color="text-body-secondary">
          {t(f.descriptionId)}
        </Box>
      </SpaceBetween>
    </Container>
  );

  return (
    <div className={styles.wrapper}>
      <SpaceBetween size="xl">
        {/* Hero */}
        <section aria-label={t('landing-hero-heading')}>
          <Container>
            <Box
              textAlign="center"
              padding={{ vertical: 'xl', horizontal: 'xxl' }}
            >
              <SpaceBetween size="m" alignItems="center">
                <img src={tsLogo} height={110} alt="" />
                <Box variant="h1" fontSize="display-l" fontWeight="bold">
                  {t('landing-hero-heading')}
                </Box>
                <Box
                  variant="h2"
                  fontSize="heading-xl"
                  fontWeight="normal"
                  color="inherit"
                >
                  <span className={styles.subtitle}>
                    {t('landing-hero-subtitle')}
                  </span>
                </Box>
                <Box variant="p" fontSize="body-m" color="text-body-secondary">
                  {t('landing-hero-body')}
                </Box>
                <SpaceBetween
                  direction="horizontal"
                  size="s"
                  alignItems="center"
                >
                  <Button
                    variant="primary"
                    iconName="arrow-right"
                    iconAlign="right"
                    href={PILOT_URL}
                    target="_blank"
                  >
                    {t('landing-hero-join-pilot')}
                  </Button>
                  <Button
                    variant="normal"
                    iconName="external"
                    iconAlign="right"
                    href={SURVEY_URL}
                    target="_blank"
                  >
                    {t('landing-hero-survey')}
                  </Button>
                </SpaceBetween>
              </SpaceBetween>
            </Box>
          </Container>
        </section>

        {/* Value Proposition */}
        <section aria-label={t('landing-value-section-aria')}>
          <Container>
            <SpaceBetween size="l">
              <SpaceBetween size="xs">
                <Box
                  variant="h2"
                  fontSize="heading-xl"
                  fontWeight="bold"
                  textAlign="center"
                >
                  {t('landing-value-heading')}
                </Box>
                <Box variant="p" color="text-body-secondary" textAlign="center">
                  {t('landing-value-body')}
                </Box>
              </SpaceBetween>
              <div className={styles.contentNarrow}>
                <ColumnLayout columns={3} variant="default">
                  <Container fitHeight={true}>
                    <SpaceBetween size="s">
                      <Box textAlign="center">
                        <Icon name="arrow-up" size="medium" variant="link" />
                      </Box>
                      <Box
                        variant="h3"
                        fontSize="heading-m"
                        fontWeight="bold"
                        textAlign="center"
                      >
                        {t('landing-value-scale-heading')}
                      </Box>
                      <Box
                        variant="p"
                        color="text-body-secondary"
                        textAlign="center"
                      >
                        {t('landing-value-scale-body')}
                      </Box>
                    </SpaceBetween>
                  </Container>
                  <Container fitHeight={true}>
                    <SpaceBetween size="s">
                      <Box textAlign="center">
                        <Icon name="ticket" size="medium" variant="success" />
                      </Box>
                      <Box
                        variant="h3"
                        fontSize="heading-m"
                        fontWeight="bold"
                        textAlign="center"
                      >
                        {t('landing-value-cost-heading')}
                      </Box>
                      <Box
                        variant="p"
                        color="text-body-secondary"
                        textAlign="center"
                      >
                        {t('landing-value-cost-body')}
                      </Box>
                    </SpaceBetween>
                  </Container>
                  <Container fitHeight={true}>
                    <SpaceBetween size="s">
                      <Box textAlign="center">
                        <Icon name="refresh" size="medium" variant="link" />
                      </Box>
                      <Box
                        variant="h3"
                        fontSize="heading-m"
                        fontWeight="bold"
                        textAlign="center"
                      >
                        {t('landing-value-support-heading')}
                      </Box>
                      <Box
                        variant="p"
                        color="text-body-secondary"
                        textAlign="center"
                      >
                        {t('landing-value-support-body')}
                      </Box>
                    </SpaceBetween>
                  </Container>
                </ColumnLayout>
              </div>
            </SpaceBetween>
          </Container>
        </section>

        {/* Features */}
        <section
          id="features"
          aria-label={t('landing-features-section-aria')}
          className={styles.anchorSection}
        >
          <Container>
            <SpaceBetween size="l">
              <SpaceBetween size="xs">
                <Box
                  variant="h2"
                  fontSize="heading-xl"
                  fontWeight="bold"
                  textAlign="center"
                >
                  {t('landing-features-heading')}
                </Box>
                <Box variant="p" color="text-body-secondary" textAlign="center">
                  {t('landing-features-body')}
                </Box>
              </SpaceBetween>
              <div className={styles.contentNarrow}>
                <ColumnLayout columns={3} variant="default">
                  {features
                    .slice(0, FEATURES_TOP_ROW_COUNT)
                    .map(renderFeatureCard)}
                </ColumnLayout>
                <div className={styles.contentNarrower}>
                  <ColumnLayout columns={2} variant="default">
                    {features
                      .slice(FEATURES_TOP_ROW_COUNT)
                      .map(renderFeatureCard)}
                  </ColumnLayout>
                </div>
              </div>
            </SpaceBetween>
          </Container>
        </section>

        {/* Who Can Use */}
        <section
          id="who"
          aria-label={t('landing-audience-section-aria')}
          className={styles.anchorSection}
        >
          <Container>
            <SpaceBetween size="l">
              <SpaceBetween size="xs">
                <Box
                  variant="h2"
                  fontSize="heading-xl"
                  fontWeight="bold"
                  textAlign="center"
                  id="who-heading"
                >
                  {t('landing-audience-heading')}
                </Box>
                <Box variant="p" color="text-body-secondary" textAlign="center">
                  {t('landing-audience-body')}
                </Box>
              </SpaceBetween>
              <ColumnLayout columns={4} variant="default">
                {userTypes.map((u) => (
                  <Container key={u.titleId} fitHeight={true}>
                    <SpaceBetween size="s">
                      <SpaceBetween
                        direction="horizontal"
                        size="s"
                        alignItems="center"
                      >
                        {u.iconSvg ? (
                          <img src={u.iconSvg} alt="" width={24} height={24} />
                        ) : (
                          u.iconName && (
                            <Icon
                              name={u.iconName}
                              size="normal"
                              variant="link"
                            />
                          )
                        )}
                        <Box
                          variant="h3"
                          fontSize="heading-m"
                          fontWeight="bold"
                        >
                          {t(u.titleId)}
                        </Box>
                      </SpaceBetween>
                      <Box variant="p" color="text-body-secondary">
                        {t(u.descriptionId)}
                      </Box>
                    </SpaceBetween>
                  </Container>
                ))}
              </ColumnLayout>
            </SpaceBetween>
          </Container>
        </section>

        {/* Roadmap */}
        <Roadmap />

        {/* AST vs LangHub */}
        <AstVsLangHub />
      </SpaceBetween>
    </div>
  );
};

export default MainContent;
